/** Position-history ghost/breadcrumb trail following a moving entity. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureCircleTexture } from '../../ui/CrispUtil';
import { distance } from '../../utils/Math';

export interface TrailRendererConfig extends VfxPrimitiveConfig {
  maxSegments: number;
  mode: 'ghost' | 'breadcrumb';
  spacingPx?: number;
  spacingMs?: number;
  fadeDurationMs: number;
  shrinkFactor: number;
  ghostTextureKey?: string;
}

export class TrailRenderer extends VfxPrimitive<TrailRendererConfig> {
  private textureKey!: string;
  private headIndex = 0;
  private active = false;
  private lastX = 0;
  private lastY = 0;
  private timeSinceSpawn = 0;

  protected allocatePool(): void {
    this.textureKey = this.config.ghostTextureKey
      ?? ensureCircleTexture(this.scene, this.config.textureSize);
    for (let i = 0; i < this.config.maxSegments; i++) {
      this.createPoolImage(this.textureKey);
    }
  }

  /** Begin emitting trail segments. Call each frame with entity position. */
  play(x: number, y: number, depth: number): void {
    this.active = true;
    this.lastX = x;
    this.lastY = y;
    this.timeSinceSpawn = 0;
    this.headIndex = 0;
  }

  /** Stop emitting new segments. Existing segments fade naturally. */
  stop(): void {
    this.active = false;
  }

  /** Must be called every frame while active to update segment positions. */
  updatePosition(x: number, y: number, depth: number, delta: number): void {
    if (!this.active) return;

    const { config } = this;
    let shouldSpawn = false;

    if (config.mode === 'breadcrumb' && config.spacingPx != null) {
      shouldSpawn = distance(x, y, this.lastX, this.lastY) >= config.spacingPx;
    } else {
      const interval = config.spacingMs ?? 60;
      this.timeSinceSpawn += delta;
      shouldSpawn = this.timeSinceSpawn >= interval;
    }

    if (shouldSpawn) {
      this.spawnSegment(x, y, depth);
      this.lastX = x;
      this.lastY = y;
      this.timeSinceSpawn = 0;
    }
  }

  private spawnSegment(x: number, y: number, depth: number): void {
    const img = this.pool[this.headIndex];
    this.headIndex = (this.headIndex + 1) % this.config.maxSegments;

    // Recycle: kill any running tween on this segment
    this.scene.tweens.killTweensOf(img);
    this.activate(img, x, y, depth);
    img.setScale(1);

    this.scene.tweens.add({
      targets: img,
      alpha: 0,
      scaleX: this.config.shrinkFactor,
      scaleY: this.config.shrinkFactor,
      duration: this.config.fadeDurationMs,
      ease: 'Linear',
      onComplete: () => this.deactivate(img),
    });
  }
}
